import type { HTMLAttributes, JSX, ReactNode } from 'react';
import { cx } from './primitives';

/**
 * Data-display components: the empty state and the loading skeleton. Every
 * string is passed in already translated (PRD NFR-006).
 *
 * `ResponsiveTable` used to live here - a table declared as columns and rows,
 * which grew its own card labels below md. Every screen on the platform builds
 * its table as ordinary JSX and hands the DOM to `TableFrame` instead, so it
 * had no caller in any app, and it went with `TableColumn` and `cardLabel`.
 */

// ---------------------------------------------------------------------------
// EmptyState
// ---------------------------------------------------------------------------

export interface EmptyStateProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Already translated. */
  title: ReactNode;
  /** One line saying what would put something here. Already translated. */
  body?: ReactNode;
  /** The action that fills it - "Add a property", "Clear filters". */
  action?: ReactNode;
}

/**
 * Nothing here, and why. An empty list and a failed request look identical to a
 * user unless the empty case says out loud that it is empty, which is why this is
 * a component and not a blank div.
 */
export function EmptyState({
  title,
  body,
  action,
  className,
  ...rest
}: EmptyStateProps): JSX.Element {
  return (
    <div {...rest} className={cx('cib-empty', className)}>
      <p className="cib-empty__title">{title}</p>
      {body !== undefined ? <p className="cib-empty__body">{body}</p> : null}
      {action}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Skeletons
// ---------------------------------------------------------------------------

export interface SkeletonRowProps {
  /** How many placeholder rows to draw. Match the page size you expect. */
  rows?: number;
  /** Cells per row. Match the table's column count so nothing shifts on arrival. */
  columns?: number;
  /**
   * Announced while the placeholder is on screen. Already translated. The bars
   * themselves are `aria-hidden`: a screen reader gets one "Loading", not forty
   * grey rectangles.
   */
  label: string;
  className?: string;
}

/**
 * The loading state for a list. Occupies the height the real rows will, so the
 * page does not jump when the data lands - the shift is what makes an operator
 * click the wrong row at 06:20.
 */
export function SkeletonRow({
  rows = 5,
  columns = 4,
  label,
  className,
}: SkeletonRowProps): JSX.Element {
  return (
    <div className={cx('cib-tablewrap', className)} aria-busy="true">
      <span className="cib-visually-hidden" role="status">
        {label}
      </span>
      <div aria-hidden="true">
        {Array.from({ length: rows }, (_, rowIndex) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length placeholder, never reordered.
          <div className="cib-skeleton-row" key={rowIndex}>
            {Array.from({ length: columns }, (_, cellIndex) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length placeholder, never reordered.
              <span className="cib-skeleton" key={cellIndex} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
